import { memo } from 'react';
import PropTypes from 'prop-types';
import { noop, isNil, isFunction } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { Button, Tooltip } from '@geneui/components';
import AccessControl from 'components/common/AccessControl';
import ActionLink from 'components/common/ActionLink';
// Import Hooks
import useHasPermission from 'hooks/UseHasPermission';
// Import  Constants
import { ReportTypesLabels, ReportStateLabels, l, ActionsIcons } from 'constants/common';
// Import Enums
import { PermissionEnum, SubscriptionSectionEnum } from 'enums';

const ReportActionsButtons = ({
    data,
    getReportVersionListPath,
    getScheduleReportPath,
    deleteReportAction,
    getCloneReportPath,
    editReportAction,
    getEditReportPath,
    infoReportAction,
    stopScheduleReportAction,
    executeReportAction,
    getExecuteReportPath,
    archiveReportAction,
    unarchiveReportAction,
    getClonePredefinedPath,
    isViewMode,
    isInfoVisible,
    closeModal,
}) => {
    const { t } = useTranslation();
    const { hasSubscriptionSection } = useHasPermission();

    const { State, IsUsed, ReportType, ArchivedDate, IsExported, HasParameters } = data;
    const IsAdHoc = ReportType === ReportTypesLabels.Report;
    const isArchive = !isNil(ArchivedDate);

    const isPredefinedAvailable =
        hasSubscriptionSection(SubscriptionSectionEnum.Predefined) && IsAdHoc && isFunction(getClonePredefinedPath);

    const IsScheduled = State === ReportStateLabels.Scheduled;
    const isCompositeReport = ReportType === ReportTypesLabels.CompositeReport;
    const isCustomerJourneyReport = ReportType === ReportTypesLabels.CustomerJourneyReport;
    const isCustomReport = ReportType === ReportTypesLabels.CustomReport;

    const isVisibleDeleteReport = () =>
        (IsAdHoc || isCompositeReport || isCustomerJourneyReport) && !IsUsed && !isViewMode && !IsExported;
    const isVisibleEditReport = () => (IsAdHoc || isCompositeReport || isCustomerJourneyReport) && !isViewMode;
    const isVisibleExecutedReports = () => !isViewMode;
    const isVisibleStopScheduleReport = () => !isViewMode && IsScheduled;
    const isVisibleReportInfo = () => (IsAdHoc || isCompositeReport || isCustomerJourneyReport) && isInfoVisible;
    const isVisibleCloneReport = () => (IsAdHoc || isCompositeReport || isCustomerJourneyReport) && !isViewMode;
    const isVisibleStart = () => !isViewMode;
    const isVisibleSchedule = () => !isViewMode;
    const isVisibleArchive = () => !isArchive && !isCustomReport;
    const isVisibleUnarchive = () => isArchive && !isCustomReport;

    return (
        <>
            {isVisibleDeleteReport() && (
                <AccessControl permissions={PermissionEnum.DeleteAddHocReport}>
                    <Tooltip text={t(l.DeleteReport)}>
                        <Button
                            icon={ActionsIcons.Delete}
                            appearance="minimal"
                            color="primary"
                            onClick={() => deleteReportAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleEditReport() && (
                <AccessControl permissions={PermissionEnum.EditAddHocReport}>
                    <Tooltip text={t(l.EditReport)}>
                        <ActionLink
                            icon={ActionsIcons.Edit}
                            appearance="minimal"
                            color="primary"
                            onClick={() => editReportAction(data)}
                            withConfirm={IsAdHoc}
                            redirectPath={getEditReportPath(data)}
                            onRedirect={closeModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleArchive() && (
                <Tooltip text={t(l.ArchiveReport)}>
                    <Button
                        icon={ActionsIcons.Archive}
                        appearance="minimal"
                        color="primary"
                        onClick={() => archiveReportAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleUnarchive() && (
                <Tooltip text={t(l.UnarchiveReport)}>
                    <Button
                        icon={ActionsIcons.UnArchive}
                        appearance="minimal"
                        color="primary"
                        onClick={() => unarchiveReportAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleExecutedReports() && (
                <Tooltip text={t(l.ExecutedReports)}>
                    <ActionLink
                        icon={ActionsIcons.ViewDoc}
                        appearance="minimal"
                        color="primary"
                        redirectPath={getReportVersionListPath(data)}
                        onRedirect={closeModal}
                    />
                </Tooltip>
            )}
            {isVisibleStopScheduleReport() && (
                <Tooltip text={t(l.StopReportSchedule)}>
                    <Button
                        icon={ActionsIcons.Unschedule}
                        appearance="minimal"
                        color="primary"
                        onClick={() => stopScheduleReportAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleSchedule() && (
                <AccessControl permissions={PermissionEnum.EditAddHocReport}>
                    <Tooltip text={t(l.ScheduleReport)}>
                        <ActionLink
                            icon={ActionsIcons.Schedule}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getScheduleReportPath(data)}
                            onRedirect={closeModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleStart() && (
                <AccessControl
                    permissions={
                        IsAdHoc || isCompositeReport
                            ? PermissionEnum.ExecuteAddHocReport
                            : PermissionEnum.ExecuteCustomReport
                    }
                >
                    <Tooltip text={t(l.ExecuteReport)}>
                        <ActionLink
                            icon={ActionsIcons.Start}
                            appearance="minimal"
                            color="primary"
                            onClick={() => executeReportAction(data)}
                            redirectPath={getExecuteReportPath(data)}
                            withConfirm={!HasParameters}
                            onRedirect={closeModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleCloneReport() && (
                <AccessControl permissions={PermissionEnum.CreateAddHocReport}>
                    <Tooltip text={t(l.CloneReport)}>
                        <ActionLink
                            icon={ActionsIcons.Clone}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getCloneReportPath(data)}
                            onRedirect={closeModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleCloneReport() && isPredefinedAvailable && (
                <AccessControl permissions={[PermissionEnum.CRMAdmin, PermissionEnum.CRMManager]}>
                    <Tooltip text={t(l.CloneAsPredefined)}>
                        <ActionLink
                            icon={ActionsIcons.Predefined}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getClonePredefinedPath(data)}
                            onRedirect={closeModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleReportInfo() && (
                <Tooltip text={t(l.ReportInfo)}>
                    <Button
                        icon={ActionsIcons.Stop}
                        appearance="minimal"
                        color="primary"
                        onClick={() => infoReportAction(data)}
                    />
                </Tooltip>
            )}
        </>
    );
};

ReportActionsButtons.propTypes = {
    data: PropTypes.object.isRequired,
    getReportVersionListPath: PropTypes.func,
    getScheduleReportPath: PropTypes.func,
    deleteReportAction: PropTypes.func,
    getCloneReportPath: PropTypes.func,
    editReportAction: PropTypes.func,
    infoReportAction: PropTypes.func,
    stopScheduleReportAction: PropTypes.func,
    executeReportAction: PropTypes.func,
    archiveReportAction: PropTypes.func,
    unarchiveReportAction: PropTypes.func,
    getClonePredefinedPath: PropTypes.func,
    closeModal: PropTypes.func,
    isViewMode: PropTypes.bool,
    isInfoVisible: PropTypes.bool,
};

ReportActionsButtons.defaultProps = {
    getReportVersionListPath: noop,
    getScheduleReportPath: noop,
    deleteReportAction: noop,
    getCloneReportPath: noop,
    editReportAction: noop,
    infoReportAction: noop,
    stopScheduleReportAction: noop,
    executeReportAction: noop,
    getExecuteReportPath: noop,
    archiveReportAction: noop,
    unarchiveReportAction: noop,
    closeModal: noop,
    isViewMode: false,
    isInfoVisible: true,
};

export default memo(ReportActionsButtons);
